/**
 * Country Localization Popup
 * Custom web component that displays a popup when user's detected country
 * doesn't match the current store location
 */

class CountryLocalization extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('.country-localization__overlay');
    this.closeButton = this.querySelector('.country-localization__close');
    this.select = this.querySelector('.country-localization__select-input');
    this.label = this.querySelector('.country-localization__select-label');
    this.labelValue = this.querySelector('.country-localization__select-label-value');
    this.button = this.querySelector('.country-localization__button');
    this.textElement = this.querySelector('.country-localization__text');
  }

  /**
   * Wait for geo-manager.js to load and be ready
   * @returns {Promise<void>}
   */
  waitForGeoManager() {
    return new Promise((resolve) => {
      // Check if functions already exist
      if (typeof window.getCountryCode === 'function' &&
          typeof window.getLocalizationData === 'function') {
        resolve();
        return;
      }

      // Poll for functions to become available
      const checkInterval = setInterval(() => {
        if (typeof window.getCountryCode === 'function' &&
            typeof window.getLocalizationData === 'function') {
          clearInterval(checkInterval);
          resolve();
        }
      }, 50);

      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        console.error('Geo-manager functions not loaded after 5 seconds');
        resolve();
      }, 5000);
    });
  }

  async connectedCallback() {
    // Skip in Shopify theme editor
    if (window.Shopify && window.Shopify.designMode) {
      console.log('[Country Popup] Skipped - in theme editor');
      return;
    }

    try {
      console.log('[Country Popup] Initializing...');

      // Wait for geo-manager functions to be available
      await this.waitForGeoManager();
      console.log('[Country Popup] Geo-manager loaded');

      // Get user's detected country code
      this.countryCode = await window.getCountryCode();
      console.log('[Country Popup] Detected country:', this.countryCode);

      // Wait for localization data to be ready
      this.localizationData = await window.getLocalizationData();
      console.log('[Country Popup] Localization data:', this.localizationData);

      // Check if detected country is in Shopify markets (to suppress popup)
      // Popup should NOT show for Shopify market countries (user is already on the right store)
      // Popup SHOULD show for external store countries (to offer redirect)
      const isInShopifyMarkets = this.localizationData.shopifyMarkets.find(
        country => country.code == this.countryCode
      );

      console.log('[Country Popup] Is detected country in Shopify markets?', !!isInShopifyMarkets);

      // Store this for popup display decision
      this.isInShopifyMarkets = !!isInShopifyMarkets;

      // Determine if popup should show
      const shouldShow = this.shouldOpenPopup();
      console.log('[Country Popup] Should show popup?', shouldShow);

      if (shouldShow) {
        console.log('[Country Popup] Opening popup...');
        await this.initForm();
        this.openPopup();
      } else {
        console.log('[Country Popup] Not showing popup - check conditions above');
      }
    } catch (error) {
      console.error('[Country Popup] Error:', error);
    }
  }

  /**
   * Check if popup should be displayed
   * @returns {boolean}
   */
  shouldOpenPopup() {
    const hasCookie = document.cookie.includes('country_selected=true');
    console.log('[Country Popup] Has cookie?', hasCookie);
    console.log('[Country Popup] All cookies:', document.cookie);

    console.log('[Country Popup] Is in Shopify markets?', this.isInShopifyMarkets);

    // Show popup ONLY if:
    // - User hasn't confirmed country before (no cookie)
    // - AND detected country is NOT in Shopify markets
    // Note: Popup SHOULD show for external store countries (to offer redirect)
    const shouldShow = !hasCookie && !this.isInShopifyMarkets;
    console.log('[Country Popup] Final decision: !hasCookie && !isInShopifyMarkets =',
                `!${hasCookie} && !${this.isInShopifyMarkets} = ${shouldShow}`);

    return shouldShow;
  }

  /**
   * Initialize form and event listeners
   */
  async initForm() {
    // Build dropdown options: primary market + external stores (avoiding duplicates)
    const dropdownOptions = [];
    const addedCodes = new Set(); // Track added country codes to avoid duplicates

    // Add primary market (not current market - always show the main market for this store instance)
    const primaryMarket = this.localizationData.shopifyMarkets.find(
      m => m.code === this.localizationData.primaryMarket
    );

    // Check if primary market is already in external stores (section blocks take priority)
    const primaryInExternal = this.localizationData.externalStores.find(
      ext => ext.code === this.localizationData.primaryMarket
    );

    if (primaryInExternal) {
      // Use external store version (has redirect functionality)
      dropdownOptions.push(primaryInExternal);
      addedCodes.add(primaryInExternal.code);
      console.log('[Country Popup] Primary market found in external stores - using section version');
    } else if (primaryMarket) {
      // Use Shopify market version
      dropdownOptions.push(primaryMarket);
      addedCodes.add(primaryMarket.code);
      console.log('[Country Popup] Primary market added from Shopify markets');
    }

    // Add all other Shopify markets (skip if already added)
    this.localizationData.shopifyMarkets.forEach(market => {
      if (!addedCodes.has(market.code)) {
        // Check if this market has an external store override
        const externalOverride = this.localizationData.externalStores.find(
          ext => ext.code === market.code
        );
        dropdownOptions.push(externalOverride || market);
        addedCodes.add(market.code);
      }
    });

    // Add all external stores (skip if already added)
    this.localizationData.externalStores.forEach(store => {
      if (!addedCodes.has(store.code)) {
        dropdownOptions.push(store);
        addedCodes.add(store.code);
      }
    });

    // Populate select dropdown
    this.select.innerHTML = dropdownOptions
      .map(country => {
        return `<option value="${country.code}">
          ${country.name}
        </option>`;
      })
      .join('\n');

    // IMPORTANT: Use detected country for display, even if it's not in the list
    // This shows "You're in Mexico" even though Mexico isn't an available option
    const displayCountryCode = this.countryCode;

    // Set dropdown default with priority:
    // 1. US (if it's current market or in external stores)
    // 2. First external store block
    // 3. Current market
    let selectCountryCode = null;

    // Check if US exists in dropdown options
    const usOption = dropdownOptions.find(country => country.code === 'US');
    if (usOption) {
      selectCountryCode = 'US';
      console.log('[Country Popup] Defaulting to US (priority default)');
    } else if (this.localizationData.externalStores[0]) {
      // If no US, use first external store
      selectCountryCode = this.localizationData.externalStores[0].code;
      console.log('[Country Popup] Defaulting to first external store:', selectCountryCode);
    } else {
      // Fallback to current market
      selectCountryCode = this.localizationData.currentCountry;
      console.log('[Country Popup] Defaulting to current market:', selectCountryCode);
    }

    this.select.value = selectCountryCode;
    this.changeCountry(selectCountryCode);

    // Update text with DETECTED country (not selected country)
    await this.updateText(displayCountryCode);

    // Handle select change
    this.select.addEventListener('change', (event) => {
      const selectedCode = event.target.value;
      this.changeCountry(selectedCode);
      // Keep text showing detected country, not selected country
      // this.updateText(selectedCode); // REMOVED - text should not change
    });

    // Handle confirm button click - Change to selected country
    this.button.addEventListener('click', () => {
      const selectedCountryCode = this.select.value;
      console.log('[Country Popup] Confirm clicked - changing to:', selectedCountryCode);

      // If user selected primary market (main store), just close popup (they're staying)
      if (selectedCountryCode === this.localizationData.primaryMarket) {
        console.log('[Country Popup] User selected primary market - staying on this store');
        this.closePopup();
        return;
      }

      // Otherwise, change to selected country (external store or different Shopify market)
      if (window.changeCountry) {
        window.changeCountry(selectedCountryCode);
      }
      this.closePopup();
    });

    // REMOVED: Close button (users must select a region)
    // REMOVED: Click outside to close (users must make a choice)
    // REMOVED: Escape key to close (users must make a choice)

    // Hide close button if popup is shown
    if (this.closeButton) {
      this.closeButton.style.display = 'none';
    }
  }

  /**
   * Update the displayed country in the select label
   * @param {string} countryCode - ISO country code
   */
  changeCountry(countryCode) {
    // Check external stores first, then Shopify markets (for current store option)
    let selectedCountry = this.localizationData.externalStores.find(
      country => country.code == countryCode
    );

    if (!selectedCountry) {
      selectedCountry = this.localizationData.shopifyMarkets.find(
        country => country.code == countryCode
      );
    }

    if (selectedCountry) {
      // Update label text
      this.labelValue.textContent = selectedCountry.name;
    }
  }

  /**
   * Update description text with dynamic country placeholders
   * Replaces [Country] and [CountryCode] with actual values
   * @param {string} countryCode - ISO country code
   */
  async updateText(countryCode) {
    if (!this.textElement) return;

    const template = this.textElement.dataset.textTemplate;
    if (!template) return;

    // Try to find country in external stores first, then Shopify markets
    let selectedCountry = this.localizationData.externalStores.find(
      country => country.code == countryCode
    );

    if (!selectedCountry) {
      selectedCountry = this.localizationData.shopifyMarkets.find(
        country => country.code == countryCode
      );
    }

    // If country not in any list (e.g., user is in an unconfigured country),
    // fetch the country name from the API
    if (!selectedCountry) {
      const countryName = await window.getUserLocation();
      selectedCountry = {
        name: countryName || countryCode, // Use name or fallback to code
        code: countryCode
      };
      console.log('[Country Popup] Country not in configured stores, using detected name:', selectedCountry);
    }

    // Replace placeholders with actual values
    let updatedText = template
      .replace(/\[Country\]/g, selectedCountry.name)
      .replace(/\[CountryCode\]/g, selectedCountry.code);

    this.textElement.innerHTML = updatedText;
  }

  /**
   * Open the popup
   */
  openPopup() {
    document.body.classList.add('has-country-popup-active');

    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('country-popup:opened'));
  }

  /**
   * Close the popup and set cookie
   * Cookie is set to remember user's choice (7 days)
   */
  closePopup() {
    document.body.classList.remove('has-country-popup-active');

    // Set cookie to remember user's choice (7 days)
    const expirationDate = new Date();
    expirationDate.setTime(expirationDate.getTime() + (7 * 24 * 60 * 60 * 1000));
    document.cookie = `country_selected=true; expires=${expirationDate.toUTCString()}; path=/`;
    console.log('[Country Popup] Cookie set - popup will not show again for 7 days');

    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('country-popup:closed'));
  }
}

// Register custom element
if (!customElements.get('country-localization')) {
  customElements.define('country-localization', CountryLocalization);
}
